'use client'

import { Dispatch, FocusEvent, SetStateAction } from 'react'
import { Cell, Checkbox, Text } from '@vinted/web-ui'

import { ErrorItem } from '@marketplace-web/shared/api-client'
import { useTracking } from '@marketplace-web/shared/event-tracker'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { DeprecatedInputText } from '@marketplace-web/shared/ui-helpers'
import {
  FieldName,
  ShippingContactType,
  ShippingContactValidationModel,
} from '@marketplace-web/shipping/shipping-contact-data'

import {
  checkoutBuyerInputEvent,
  transactionSellerInputEvent,
} from '_libs/common/event-tracker/events'

import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'

type Props = {
  transactionId: number | null
  validations?: Array<ShippingContactValidationModel>
  value: string | null | undefined
  persist: boolean
  isCompleted: boolean
  type: ShippingContactType
  errors: Array<ErrorItem>
  setErrors: Dispatch<SetStateAction<Array<ErrorItem>>>
  onBlur: (value: string) => void
  onPersist: () => void
}

const PhoneNumber = ({
  transactionId,
  validations,
  value,
  persist,
  isCompleted,
  type,
  errors,
  setErrors,
  onBlur,
  onPersist,
}: Props) => {
  const { track } = useTracking()
  const translate = useTranslate(`shipping_contact.${type}.form.phone_number`)

  const fieldName =
    type === ShippingContactType.Buyer ? FieldName.BuyerPhoneNumber : FieldName.SellerPhoneNumber

  const error = errors.find(({ field }) => field === fieldName)

  const trackBlur = (isValid: boolean, trackingTarget?: string) => {
    if (!transactionId) return

    if (type === ShippingContactType.Buyer) {
      track(
        checkoutBuyerInputEvent({
          screen: Screen.ContactDetails,
          target: 'phone_number',
          transactionId,
          valid: isValid,
          targetDetails: trackingTarget,
        }),
      )

      return
    }

    track(
      transactionSellerInputEvent({
        screen: Screen.SellerContactDetails,
        target: ClickableElement.SavePhoneNumber,
        valid: isValid,
        transactionId,
        targetDetails: trackingTarget,
      }),
    )
  }

  const getFirstFailedValidation = (inputValue: string) => {
    return validations?.find(({ regex }) => !new RegExp(regex).test(inputValue))
  }

  const handleFailedValidation = (failedValidation: ShippingContactValidationModel) => {
    const { trackingTarget, message } = failedValidation

    trackBlur(false, trackingTarget)

    setErrors(prevErrors => [...prevErrors, { field: fieldName, value: message }])
  }

  const handleValidInput = (inputValue: string) => {
    trackBlur(true)
    onBlur(inputValue)

    if (!error) return

    setErrors(prevErrors => prevErrors.filter(({ field }) => field !== fieldName))
  }

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    const failedValidation = getFirstFailedValidation(event.target.value)

    if (failedValidation) {
      handleFailedValidation(failedValidation)

      return
    }

    handleValidInput(event.target.value)
  }

  const renderPersistenceCheck = () => (
    <Cell
      prefix={
        <Checkbox
          testId="save-for-later"
          name={`${fieldName}_persist`}
          onClick={onPersist}
          checked={persist}
          onChange={() => undefined}
        />
      }
      body={isCompleted ? translate('save_for_later.update') : translate('save_for_later.add')}
    />
  )

  return (
    <>
      <DeprecatedInputText
        name={fieldName}
        title={translate('title')}
        placeholder={translate('placeholder')}
        value={value}
        onBlur={handleBlur}
        validation={
          error && <Text as="h4" text={error.value} type={Text.Type.Caption} theme="warning" />
        }
      />
      {renderPersistenceCheck()}
    </>
  )
}

export default PhoneNumber
